import React, { useEffect, useState } from 'react';
import { certificatesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { History, Link2Off, Replace } from 'lucide-react';
import { toast } from 'sonner';

export const isSuperseded = (c) => c?.status === 'superseded';

const NONE = '__none__';
const sameHolder = (a, b) => (a.component_id ? a.component_id === b.component_id : (!b.component_id && a.asset_id === b.asset_id));
const label = (c) => `${c.cert_number} · ${c.certificate_type || 'Certificate'}${c.expiry_date ? ` · exp ${String(c.expiry_date).slice(0, 10)}` : ''}`;

// "Replaces certificate" picker for the add form. `certs` = current certificates on the same asset.
export const ReplacesPicker = ({ certs, certificateType, value, onChange }) => {
  const [touched, setTouched] = useState(false);
  const options = (certs || []).filter(c => !isSuperseded(c) && !c.component_id);
  useEffect(() => {
    if (touched || !certificateType) return;
    const t = certificateType.trim().toLowerCase();
    const match = options.find(c => (c.certificate_type || '').trim().toLowerCase() === t);
    onChange(match ? match.id : null);
  }, [certificateType, touched, certs]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!options.length) return null;
  return (
    <div className="space-y-1.5" data-testid="cert-replaces-picker">
      <Label className="label-overline">Replaces certificate</Label>
      <Select value={value || NONE} onValueChange={(v) => { setTouched(true); onChange(v === NONE ? null : v); }}>
        <SelectTrigger data-testid="cert-replaces-select"><SelectValue placeholder="Not replacing anything" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE} data-testid="cert-replaces-none">Not replacing anything</SelectItem>
          {options.map(c => <SelectItem key={c.id} value={c.id} data-testid={`cert-replaces-option-${c.id}`}>{label(c)}</SelectItem>)}
        </SelectContent>
      </Select>
      {value && <p className="text-[11px] text-muted-foreground">The selected certificate will be kept for history and no longer count as expired or expiring.</p>}
    </div>
  );
};

// Links + actions on a certificate: Replaces / Replaced by, "Mark as replaced by…", admin Unlink.
export const ReplacementLinks = ({ cert, certs, onChanged }) => {
  const { isAdmin } = useAuth();
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const candidates = (certs || []).filter(c => c.id !== cert.id && !isSuperseded(c) && !c.replaces_cert_id && sameHolder(cert, c));

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setPicking(false); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not update certificate'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2 text-[12px]" data-testid={`cert-replacement-${cert.id}`}>
      {cert.replaces_cert_number && (
        <p className="flex items-center gap-1.5 text-muted-foreground" data-testid={`cert-replaces-${cert.id}`}>
          <History className="w-3.5 h-3.5" /> Replaces <span className="font-mono font-semibold">{cert.replaces_cert_number}</span>
        </p>
      )}
      {isSuperseded(cert) ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-muted-foreground" data-testid={`cert-replaced-by-${cert.id}`}>
            Replaced by <span className="font-mono font-semibold">{cert.superseded_by_number || '—'}</span>
          </span>
          {isAdmin() && (
            <Button type="button" size="sm" variant="outline" disabled={busy} data-testid={`cert-unlink-btn-${cert.id}`}
              onClick={() => run(() => certificatesApi.unsupersede(cert.id), 'Replacement undone — certificate is current again')}>
              <Link2Off className="w-3.5 h-3.5 mr-1.5" /> Unlink
            </Button>
          )}
        </div>
      ) : candidates.length > 0 && (
        picking ? (
          <div className="flex items-center gap-2">
            <Select onValueChange={(v) => run(() => certificatesApi.supersede(cert.id, v), `${cert.cert_number} marked as replaced`)} disabled={busy}>
              <SelectTrigger className="h-8 max-w-xs" data-testid={`cert-mark-replaced-select-${cert.id}`}><SelectValue placeholder="Choose the new certificate…" /></SelectTrigger>
              <SelectContent>
                {candidates.map(c => <SelectItem key={c.id} value={c.id} data-testid={`cert-mark-replaced-option-${c.id}`}>{label(c)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="button" size="sm" variant="ghost" onClick={() => setPicking(false)}>Cancel</Button>
          </div>
        ) : (
          <Button type="button" size="sm" variant="outline" onClick={() => setPicking(true)} data-testid={`cert-mark-replaced-btn-${cert.id}`}>
            <Replace className="w-3.5 h-3.5 mr-1.5" /> Mark as replaced by…
          </Button>
        )
      )}
    </div>
  );
};
